/**
 * Barberos y barberías guardados por el cliente.
 *
 * Viven en SU navegador y en ningún otro sitio: no hay cuenta que crear ni
 * nada que el servidor sepa de quién ha marcado a quién. El precio de eso es
 * que la lista no sigue a nadie de móvil a ordenador, y eso se dice en la
 * pantalla en vez de dejar que lo descubran perdiéndola.
 *
 * Se guarda una COPIA de la ficha (nombre, foto, ciudad) para que la lista se
 * pinte al instante y sin conexión, y luego se refresca contra la API: una
 * ficha guardada envejece —cambian la foto, el precio, cierran— y unos
 * favoritos que llevan a un 404 son peores que no tenerlos.
 */
export type FavoriteType = 'barber' | 'shop'

export interface Favorite {
  type: FavoriteType
  slug: string
  name: string
  subtitle?: string | null
  avatar_url?: string | null
  city?: string | null
  saved_at: string
}

const CLAVE = 'barberman.favoris.v1'

// Tope por si alguien se dedica a marcar todo: el almacenamiento del navegador
// no es infinito y una lista de mil no la lee nadie.
const MAXIMO = 60

function leer(): Favorite[] {
  try {
    const crudo = window.localStorage.getItem(CLAVE)
    if (!crudo) return []

    const datos = JSON.parse(crudo)
    if (!Array.isArray(datos)) return []

    return datos.filter((f: any): f is Favorite =>
      f && typeof f.slug === 'string' && (f.type === 'barber' || f.type === 'shop'))
  } catch {
    // Modo privado, almacenamiento lleno o un JSON que alguien tocó a mano.
    // Ninguno es motivo para romper la página.
    return []
  }
}

export function useFavorites() {
  const items = useState<Favorite[]>('favoritos', () => [])
  const ready = useState<boolean>('favoritos:listo', () => false)

  function guardar() {
    try {
      window.localStorage.setItem(CLAVE, JSON.stringify(items.value))
    } catch {
      // Si no se puede escribir, la sesión actual sigue funcionando; lo único
      // que se pierde es que sobreviva al cierre del navegador.
    }
  }

  onMounted(() => {
    if (ready.value) return

    items.value = leer()
    ready.value = true

    // Dos pestañas abiertas son el mismo cliente: lo que marca en una tiene
    // que aparecer en la otra.
    window.addEventListener('storage', (e) => {
      if (e.key === CLAVE) items.value = leer()
    })
  })

  const has = (type: FavoriteType, slug: string) =>
    items.value.some(f => f.type === type && f.slug === slug)

  function toggle(fav: Omit<Favorite, 'saved_at'>) {
    if (has(fav.type, fav.slug)) {
      items.value = items.value.filter(f => !(f.type === fav.type && f.slug === fav.slug))
    } else {
      items.value = [{ ...fav, saved_at: new Date().toISOString() }, ...items.value].slice(0, MAXIMO)
    }

    guardar()
    return has(fav.type, fav.slug)
  }

  function remove(type: FavoriteType, slug: string) {
    items.value = items.value.filter(f => !(f.type === type && f.slug === slug))
    guardar()
  }

  /** Sustituye la copia guardada por la de la API, sin perder el orden. */
  function refresh(frescos: Favorite[]) {
    const porClave = new Map(frescos.map(f => [`${f.type}:${f.slug}`, f]))

    const vivos: Favorite[] = []
    const perdidos: Favorite[] = []

    for (const guardado of items.value) {
      const fresco = porClave.get(`${guardado.type}:${guardado.slug}`)
      fresco ? vivos.push({ ...fresco, saved_at: guardado.saved_at }) : perdidos.push(guardado)
    }

    items.value = vivos
    guardar()

    // Se devuelven los que ya no están para poder decir POR QUÉ desaparecieron
    // en vez de borrarlos a la chita callando.
    return perdidos
  }

  const barbers = computed(() => items.value.filter(f => f.type === 'barber'))
  const shops = computed(() => items.value.filter(f => f.type === 'shop'))

  return { items, ready, barbers, shops, has, toggle, remove, refresh, count: computed(() => items.value.length) }
}
